import type {
  SupportSessionOutcome,
  SupportSessionState,
} from "@decizyon/domain";

export interface PageQuery {
  readonly cursor?: string;
  readonly limit?: number;
}

export interface ProductAgentBindingQuery extends PageQuery {
  readonly productId?: string;
}

export interface TicketListQuery extends PageQuery {
  readonly productId?: string;
}

export interface SupportSessionListQuery extends PageQuery {
  readonly productId?: string;
  readonly state?: SupportSessionState;
  readonly outcome?: SupportSessionOutcome;
}

function compactQuery(
  entries: readonly (readonly [string, string | number | undefined])[],
): Readonly<Record<string, string | number>> {
  return Object.freeze(
    Object.fromEntries(entries.filter((entry) => entry[1] !== undefined)),
  ) as Readonly<Record<string, string | number>>;
}

function key<const T extends readonly unknown[]>(...parts: T): Readonly<T> {
  return Object.freeze(parts);
}

export const queryKeys = Object.freeze({
  products: () => key("products"),
  productList: (query: PageQuery = {}) =>
    key(
      "products",
      "list",
      compactQuery([
        ["cursor", query.cursor],
        ["limit", query.limit],
      ]),
    ),
  productAgentBindings: (query: ProductAgentBindingQuery = {}) =>
    key(
      "product-agent-bindings",
      "list",
      compactQuery([
        ["productId", query.productId],
        ["cursor", query.cursor],
        ["limit", query.limit],
      ]),
    ),
  tickets: () => key("tickets"),
  ticketList: (query: TicketListQuery = {}) =>
    key(
      "tickets",
      "list",
      compactQuery([
        ["productId", query.productId],
        ["cursor", query.cursor],
        ["limit", query.limit],
      ]),
    ),
  ticket: (ticketId: string) => key("tickets", "detail", ticketId),
  supportSessions: () => key("support-sessions"),
  supportSessionList: (query: SupportSessionListQuery = {}) =>
    key(
      "support-sessions",
      "list",
      compactQuery([
        ["productId", query.productId],
        ["state", query.state],
        ["outcome", query.outcome],
        ["cursor", query.cursor],
        ["limit", query.limit],
      ]),
    ),
  supportSession: (sessionId: string) =>
    key("support-sessions", "detail", sessionId),
});
